import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {useAddressStore} from '@/stores/AddressStore'
import {usePaymentGatewaysStore} from "@/stores/PaymentGatewaysStore"
import {useSubscriptionsStore} from "@/stores/SubscriptionsStore"
import {useVoucherStore} from "@/stores/VoucherStore"
import {useHelpers} from "@/useHelpers"

export const useFormDataStore = defineStore('FormDataStore', () => {

	const { inIframe, sendMessage, addDefaultPhonePrefixIntoAddress } = useHelpers()

	const addressStore = useAddressStore()
	const paymentGatewaysStore = usePaymentGatewaysStore()
	const subscriptionsStore = useSubscriptionsStore()
	const voucherStore = useVoucherStore()

	const formData = ref({
		email: window.init.email || '',
		subscriptionCode: '',
		variantSubscriptionCode: '',
		paymentGatewayCode: '',
		address: addDefaultPhonePrefixIntoAddress(window.init.addresses?.paper),
		sendInvoice: false,
		invoiceAddress: addDefaultPhonePrefixIntoAddress(window.init.addresses?.invoice),
		subscriptionAmount: null,
		giftedEmail: '',
		giftedDateStart: '',
		pickupPoint: null,
	})

	const isVariantTrial = computed(() => {
		return formData.value.variantSubscriptionCode.includes('trial')
	})

	const selectedPaymentGateway = computed(() => {
		if(formData.value.paymentGatewayCode) {
			return paymentGatewaysStore.getGatewayByCode(formData.value.paymentGatewayCode)
		}

		return null
	})

	const selectedSubscription = computed(() => {
		if(formData.value.subscriptionCode) {
			return subscriptionsStore.getSubscriptionByCode(formData.value.subscriptionCode)
		}

		return null
	})

	const editorialPlan = computed(() => {
		if (selectedSubscription.value) {
			return selectedSubscription.value.product?.print_dates
		}
		return null
	})

	const editorialPlanFirstDate = computed(() => {
		if(editorialPlan.value) {
			return Object.keys(editorialPlan.value)[0] || ''
		}

		return ''
	})

	const selectedSubscriptionWithVariant = computed(() => {
		if(formData.value.variantSubscriptionCode) {
			return subscriptionsStore.getSubscriptionByCode(formData.value.variantSubscriptionCode)
		}

		if(formData.value.subscriptionCode) {
			return subscriptionsStore.getSubscriptionByCode(formData.value.subscriptionCode)
		}

		return null
	})

	const setSelectedSubscription = subscriptionCode => {
		formData.value.subscriptionCode = subscriptionCode

		sendMessage('subscriptionChanged', {
			subscription: selectedSubscription.value?.code
		})
	}

	const setVariantSubscription = subscriptionCode => {
		formData.value.variantSubscriptionCode = subscriptionCode
	}

	const setEmail = email => {
		formData.value.email = email
	}

	const setSelectedPaymentGateway = paymentGatewayCode => {
		formData.value.paymentGatewayCode = paymentGatewayCode
	}

	const setSendInvoice = sendInvoice => {
		formData.value.sendInvoice = !!sendInvoice
	}

	const setAddress = address => {
		formData.value.address = address
	}

	const setInvoiceAddress = invoiceAddress => {
		formData.value.invoiceAddress = invoiceAddress
	}

	const setPacketaPickupPoint = pickupPoint => {
		formData.value.pickupPoint = pickupPoint
	}

	const setSubscriptionAmount = amount => {
		formData.value.subscriptionAmount = amount
	}

	const setGiftedEmail = email => {
		formData.value.giftedEmail = email
	}

	const setGiftedStartDate = date => {
		formData.value.giftedDateStart = date
	}

	const generateForm = async () => {
		const elForm = document.getElementById('mainForm')
		elForm.setAttribute('method', 'POST')
		if(inIframe()) {
			elForm.setAttribute('target', '_top')
		}
		elForm.setAttribute('action', '/sales-funnel/sales-funnel-frontend/submit')

		let input = document.createElement('input')
		input.setAttribute('type', 'hidden')
		input.setAttribute('name', `funnel_url_key`)
		input.setAttribute('value', window.init.funnelUrlKey)
		elForm.appendChild(input)

		input = document.createElement('input')
		input.setAttribute('type', 'hidden')
		input.setAttribute('name', `subscription_type`)
		input.setAttribute('value', selectedSubscriptionWithVariant.value.code)
		elForm.appendChild(input)

		input = document.createElement('input')
		input.setAttribute('type', 'hidden')
		input.setAttribute('name', `payment_gateway`)
		input.setAttribute('value', selectedPaymentGateway.value.code)
		elForm.appendChild(input)

		input = document.createElement('input')
		input.setAttribute('type', 'hidden')
		input.setAttribute('name', `email`)
		input.setAttribute('value', formData.value.email)
		elForm.appendChild(input)

		if(voucherStore.voucher) {
			try {
				const activatedVoucher = await voucherStore.activateVoucher()
				console.log('🚀 ~ file: FormDataStore.js ~ line 152 ~ generateForm ~ activatedVoucher', activatedVoucher.data)

				input = document.createElement('input')
				input.setAttribute('type', 'hidden')
				input.setAttribute('name', `payment_metadata[voucher_log_id]`)
				input.setAttribute('value', activatedVoucher.data.voucher_log_id)

				elForm.appendChild(input)
			} catch(e) {
				console.log('🚀 ~ file: FormDataStore.js ~ line 154 ~ generateForm ~ e', e)
			}

			input = document.createElement('input')
			input.setAttribute('type', 'hidden')
			input.setAttribute('name', `payment_metadata[voucher_code]`)
			input.setAttribute('value', voucherStore.voucher.code)

			elForm.appendChild(input)
		}

		if (addressStore.packetaDelivery && formData.value.pickupPoint) {
			for (const [field, value] of Object.entries(formData.value.pickupPoint)) {
				input = document.createElement('input')
				input.setAttribute('type', 'hidden')
				input.setAttribute('name', `payment_metadata[pickupPoint-data_${field}]`)
				input.setAttribute('value', value)

				elForm.appendChild(input)
			}
		}

		if (formData.value.address) {
			for (const [field, value] of Object.entries(formData.value.address)) {
				input = document.createElement('input')
				input.setAttribute('type', 'hidden')
				input.setAttribute('name', `payment_metadata[print-data_${field}]`)
				input.setAttribute('value', value)

				elForm.appendChild(input)
			}
		}

		if (formData.value.invoiceAddress) {
			for (let [field, value] of Object.entries(formData.value.invoiceAddress)) {
				input = document.createElement('input')

				// CRM has this 2 company fields without prefix, but we expect them with company prefix in our handlers
				if(['tax_id', 'vat_id'].includes(field)) {
					field = `company_${field}`
				}

				input.setAttribute('type', 'hidden')
				input.setAttribute('name', `payment_metadata[invoice-data_${field}]`)
				input.setAttribute('value', value)

				elForm.appendChild(input)
			}
		}

		//Business subscription handling
		if(formData.value.subscriptionAmount) {
			input = document.createElement('input')
			input.setAttribute('type', 'hidden')
			input.setAttribute('name', `payment_metadata[business_subscription_amount]`)
			input.setAttribute('value', formData.value.subscriptionAmount)

			elForm.appendChild(input)

			input = document.createElement('input')
			input.setAttribute('type', 'hidden')
			input.setAttribute('name', `payment_metadata[company_gift]`)
			input.setAttribute('value', '1')

			elForm.appendChild(input)

			input = document.createElement('input')
			input.setAttribute('type', 'hidden')
			input.setAttribute('name', `payment_metadata[company_subscriptions_count]`)
			input.setAttribute('value', formData.value.subscriptionAmount)

			elForm.appendChild(input)

			input = document.createElement('input')
			input.setAttribute('type', 'hidden')
			input.setAttribute('name', `payment_metadata[company_subscription_type]`)
			input.setAttribute('value', selectedSubscription.value.code)

			elForm.appendChild(input)
		}

		//Gift subscription handling
		if(window.init.funnelUrlKey === 'gift') {
			input = document.createElement('input')
			input.setAttribute('type', 'hidden')
			input.setAttribute('name', `payment_metadata[gift]`)
			input.setAttribute('value', '1')
			elForm.appendChild(input)

			input = document.createElement('input')
			input.setAttribute('type', 'hidden')
			input.setAttribute('name', `payment_metadata[gift_email]`)
			input.setAttribute('value', window.init.email)
			elForm.appendChild(input)

			input = document.createElement('input')
			input.setAttribute('type', 'hidden')
			input.setAttribute('name', `payment_metadata[gift_starts_at]`)
			input.setAttribute('value', new Date().toISOString().replace('Z', 'z'))
			elForm.appendChild(input)
		}

		/*
		 * TODO Add 'formData.sendInvoice' to form
		 */
	}

	const submitForm = async () => {
		await generateForm()
		document.getElementById('mainForm').submit()
	}

	return {
		formData,
		selectedPaymentGateway,
		selectedSubscription,
		selectedSubscriptionWithVariant,
		editorialPlan,
		editorialPlanFirstDate,
		isVariantTrial,
		setEmail,
		setSelectedPaymentGateway,
		setSelectedSubscription,
		setVariantSubscription,
		setSubscriptionAmount,
		setSendInvoice,
		setAddress,
		setInvoiceAddress,
		setGiftedEmail,
		setGiftedStartDate,
		setPacketaPickupPoint,
		submitForm
	}
})